import React, { useEffect, useState } from 'react'
import { DotsThree, Gift, PencilSimple, Trash } from '@phosphor-icons/react'
import {
  useChangeDiscountStatus,
  useGetDiscountByLeagueId,
} from 'api/league/LeagueDiscount/leagueDiscount'
import Chip from 'components/global/Chip'
import CustomAvatar from 'components/global/CustomAvatar'
import Spinner from 'components/Spinner'
import moment from 'moment'
import { leagueEventStore } from 'state/league/leagueEvent'
import { Dropdown, MenuProps } from 'antd'
import { LeagueDiscountResp } from 'types/league/leagueDiscount'
import { manageTeamStore } from 'state/league/manageTeam'
import AlertModal from 'helper/AlertModal'

const ManageDiscount: React.FC = () => {
  const { leagueData } = leagueEventStore(store => ({
    leagueData: store.leagueData,
  }))
  const { selectedDiscount, setSelectedDiscount, setAddDiscountModal } =
    manageTeamStore(store => ({
      selectedDiscount: store.selectedDiscount,
      setSelectedDiscount: store.setSelectedDiscount,
      setAddDiscountModal: store.setAddDiscountModal,
    }))
  const [alertModal, setAlertModal] = useState<boolean>(false)
  const {
    isLoading,
    isFetching,
    refetch,
    isError,
    data: discountList,
  } = useGetDiscountByLeagueId(leagueData._id)
  const {
    mutate: changeDiscountStatus,
    isLoading: changeDiscountStatusLoading,
  } = useChangeDiscountStatus()

  const items = [
    {
      key: 'edit',
      label: (
        <div className="flex align-center gap-10px">
          <PencilSimple size={20} color="#000" />
          <span>Edit</span>
        </div>
      ),
    },
    {
      key: 'delete',
      label: (
        <div className="flex align-center gap-10px">
          <Trash size={20} color="#ff6961" />
          <span className="danger-txt">Delete</span>
        </div>
      ),
    },
  ]

  const handleYesDelete = () => {
    changeDiscountStatus(
      { discountCodeId: selectedDiscount._id, payload: { status: 'archived' } },
      {
        onSuccess: () => {
          setSelectedDiscount(null)
          setAlertModal(false)
        },
      },
    )
  }

  const handleMenuClick = (
    e: Parameters<MenuProps['onClick']>[0],
    item: LeagueDiscountResp,
  ) => {
    setSelectedDiscount(item)
    if (e.key === 'edit') {
      setAddDiscountModal(true)
    } else {
      setAlertModal(true)
    }
  }

  useEffect(() => {
    refetch()
  }, [])

  if (isError) {
    return (
      <div className="flex justify-center items-center py-40px">
        <p>Something went wrong while loading teams</p>
      </div>
    )
  }

  return (
    <>
      {isLoading || isFetching ? (
        <div className="py-40px felx justify-center items-center">
          <Spinner />
        </div>
      ) : discountList.length > 0 ? (
        discountList.map(item => {
          return (
            <div
              className="px-16px py-12px radius-12 shadow w-98p mx-auto mt-16px"
              key={item._id}
            >
              <div className="flex align-center justify-between gap-20px pb-12px border-btm">
                <div className="flex align-center gap-16px">
                  <div className="flex align-center justify-center px-10px py-10px border-1px radius-8">
                    <Gift size={32} weight="thin" />
                  </div>
                  <div>
                    <h3>{item.code}</h3>
                    <div className="flex align-center flex-wrap gap-10px">
                      <Chip
                        text={moment(item.expiry).format('DD MMM, YYYY')}
                        isGray
                      />
                      <Chip
                        text={`${item.discount}${
                          item.type === 'percent' ? '%' : 'usd'
                        }`}
                        isGray
                      />
                    </div>
                  </div>
                </div>
                <Dropdown
                  trigger={['click']}
                  menu={{ items, onClick: e => handleMenuClick(e, item) }}
                  placement="bottomRight"
                  arrow={false}
                  overlayStyle={{ width: '150px' }}
                >
                  <DotsThree size={20} color="#000" className="pointer" />
                </Dropdown>
              </div>

              {item.leagueTeams.map(team => {
                return (
                  <div
                    className="flex align-center gap-16px pt-12px"
                    key={team.id}
                  >
                    <div>
                      <CustomAvatar
                        src={team.team.profilePhoto?.thumb}
                        size={40}
                        name={team.team.name}
                      />
                    </div>
                    <h3>{team.team.name}</h3>
                  </div>
                )
              })}
            </div>
          )
        })
      ) : (
        <div className="mt-20px">
          <p className="text-gray">No discount codes created yet</p>
        </div>
      )}

      {alertModal && (
        <AlertModal
          isModalVisible={alertModal}
          setIsModalVisible={setAlertModal}
          data={{
            name: 'Discount Code',
            heading: 'Delete Discount Code',
            message1: 'Are you sure you want to delete this discount code?',
            okText: 'Yes, delete',
          }}
          onYesClick={handleYesDelete}
          loading={changeDiscountStatusLoading}
        />
      )}
    </>
  )
}

export default ManageDiscount
